import React, { PropsWithChildren, ReactNode, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useUserContext } from '../contexts/UserContext';
import PersonCropCircle from '../components/icons/PersonCropCircle';
import SmallActionButton from '../components/buttons/SmallActionButton';
import { loginUrl } from '../lib/login';
import ShineLogo from '../components/icons/ShineLogo';
import { useBackgroundContext } from '../contexts/BackgroundContext';
import CoinsTooltip from '../components/coins/CoinsTooltip';
import Gallery from '../components/icons/Gallery';

import { DeviceQueries } from 'Common/src/components/styled';
import SunshineCoin from 'Common/src/components/icons/SunshineCoin';
import { ULogApplication, ULogSeverity, ULogTag, logSumoEvent } from 'Common/src/api/SumoLogicApi';
import HeaderActionButton from '../components/buttons/HeaderActionButton';
import { parseName } from '../util/attendee';

declare const ELK_COINS_ENABLED: boolean;

const BasePageHeader = ({ children, hideLogin }: PropsWithChildren<{
  hideLogin?: boolean
}>) => {
  const userContext = useUserContext();
  const backgroundContext = useBackgroundContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [firstName] = parseName(userContext.name);

  const onLogin = () => {
    if (location.pathname === '/welcome') {
      navigate(loginUrl('/'));
    } else {
      navigate(loginUrl(location.pathname + location.search));
    }
  };

  const onNew = () => {
    navigate('/event/create');
  };

  const onProfileClick = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[BasePageHeader] Clicked on profile edit link'
    });
  }

  const backgroundTextColor = backgroundContext.systemColors.backgroundText;
  const logoColor = backgroundContext.colors.highlightText;

  let rightContent: ReactNode = null;
  if (userContext.isLoggedIn()) {
    rightContent = <NavContainer>
      {ELK_COINS_ENABLED &&
        <IconContainer>
          <CoinsTooltip>
            <SunshineCoin color={backgroundTextColor}/>
          </CoinsTooltip>
        </IconContainer>}
      <IconContainer>
        <HeaderLink to="/gallery">
          <Gallery size={45} color={backgroundTextColor}/>
        </HeaderLink>
      </IconContainer>
      <IconContainer>
        <HeaderLink to="/profile/edit" onClick={onProfileClick}>
          <PersonCropCircle size={30} color={backgroundTextColor}/>
        </HeaderLink>
      </IconContainer>
    </NavContainer>;
  } else if (!hideLogin) {
    rightContent = <NavContainer>
      {ELK_COINS_ENABLED &&
        <IconContainer>
          <CoinsTooltip>
            <SunshineCoin/>
          </CoinsTooltip>
        </IconContainer>}
      <IconContainer>
        <HeaderLink to="/gallery">
          <Gallery size={45} color={backgroundTextColor}/>
        </HeaderLink>
      </IconContainer>
      <HeaderActionButton onClick={onLogin}>Login</HeaderActionButton>
    </NavContainer>;
  }

  return <div>
    <BoxContainer>
      <HeaderItem>
        <HeaderLink to="/"><ShineLogo color={logoColor} opacity={1}/></HeaderLink>
      </HeaderItem>
      <HeaderItem>
        {children !== undefined ? <ChildrenContainer>{children}</ChildrenContainer> : null}
        <ExtraHeader>
          {firstName && <WelcomeText>Hi {firstName}</WelcomeText>}
          <HeaderActionButton onClick={onNew}>Create Invite</HeaderActionButton>
          {rightContent}
        </ExtraHeader>
      </HeaderItem>
    </BoxContainer>
  </div>;
};

const BoxContainer = styled.div`
  position: relative;
  width: calc(100vw - 50px);
  background-color: rgb(235, 235, 235, 0.0);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 25px 25px 10px 25px;
  gap: 10px;
  color: var(--shine-background-text-color);
  font-family: Poppins, sans-serif;

  @media (${DeviceQueries.mobile}) {
    width: calc(100vw - 20px);
    padding: 10px 5px 5px;
  }
`;

const HeaderLink = styled(Link)`
  color: var(--shine-background-text-color);
`;

const HeaderItem = styled.div`
  align-items: center;
  display: flex;

  @media (${DeviceQueries.mobile}) {
    font-size: 8pt;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;

  a {
    display: flex;
  }
`;

const NavContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  height: 35px;
`;

const ChildrenContainer = styled.div`
  margin-right: 10px;
`;

const ExtraHeader = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const WelcomeText = styled.div`
  font-size: 14px;
  font-family: Poppins, sans-serif;

  @media (${DeviceQueries.mobile}) {
    font-size: 12px;
  }
`;


export default BasePageHeader;
